import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, PoundSterling } from 'lucide-react';
import { balanceDue } from './driverUtils';

const METHODS = [
  { key: 'cash', label: 'Cash' },
  { key: 'card', label: 'Card' },
  { key: 'bank', label: 'Bank transfer' },
];

export const BalancePanel = ({ job, authFetch, onUpdated }) => {
  const bc = job.balance_collected;
  const [method, setMethod] = useState('cash');
  const [amount, setAmount] = useState(String(balanceDue(job)));
  const [busy, setBusy] = useState(false);

  const submit = async (payload, msg) => {
    setBusy(true);
    try {
      await authFetch('post', `/jobs/${job.id}/balance`, payload);
      toast.success(msg);
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save payment.");
    } finally { setBusy(false); }
  };

  if (bc) {
    const label = METHODS.find(m => m.key === bc.method)?.label || bc.method;
    return (
      <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 flex items-center justify-between gap-2" data-testid={`balance-collected-${job.ref}`}>
        <div className="text-sm text-emerald-900"><strong>£{bc.amount}</strong> collected · {label}</div>
        <button type="button" onClick={() => submit({ method: null }, 'Payment record cleared.').then(() => setAmount(String(balanceDue(job))))} disabled={busy} className="h-10 px-3 text-xs font-bold text-emerald-800 underline" data-testid={`balance-undo-${job.ref}`}>Undo</button>
      </div>
    );
  }

  const save = () => {
    const a = parseInt(amount, 10);
    if (Number.isNaN(a) || a < 0) return toast.error('Enter the amount collected.');
    submit({ method, amount: a }, `£${a} balance recorded.`);
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2" data-testid={`balance-panel-${job.ref}`}>
      <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">Balance on the day · £{balanceDue(job)} due</div>
      <div className="grid grid-cols-3 gap-2">
        {METHODS.map(m => (
          <button key={m.key} type="button" onClick={() => setMethod(m.key)} className={`h-11 rounded-md text-sm font-bold border transition-colors ${method === m.key ? 'bg-[color:var(--mv-navy)] text-white border-[color:var(--mv-navy)]' : 'bg-white text-slate-700 border-slate-200'}`} data-testid={`balance-method-${m.key}-${job.ref}`}>
            {m.label}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <PoundSterling className="w-4 h-4 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <input type="number" min="0" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full h-11 rounded-md border border-slate-200 pl-7 pr-2 text-base" data-testid={`balance-amount-${job.ref}`} />
        </div>
        <button type="button" onClick={save} disabled={busy} className="h-11 px-4 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold inline-flex items-center gap-1.5" data-testid={`balance-save-${job.ref}`}>
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Balance collected
        </button>
      </div>
    </div>
  );
};
